import { useState, useCallback, useRef, useEffect } from "react"
import { IDataNascimentoFilter } from "../../../../../../../application/models"
import { useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles"
import { useUIService } from "../../../../../../../use-cases/ui"
import { Grid } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { ptBR } from "date-fns/locale"
import { useAppSelector } from "../../../../../../hooks/store-hooks"

export const RecorteDataNascimento = () => {
    const { createRecorteExtracaoForm } = useAppSelector((state) => state.recorteExtracao)
    // Use State
    const [selectedDataNascimento, setSelectedDataNascimento] = useState<IDataNascimentoFilter>({})
    const minFieldTouched = useRef(false)
    const maxFieldTouched = useRef(false)

    // Utils
    const today = new Date();
    const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())

    // Hooks
    const { syncFilterWithForm, removeFilterFromForm } = useRecortesExtracaoService()
    const { setAlert } = useUIService()

    // Sincronização inicial com o estado global
    useEffect(() => {
        const dataNascimentoFromForm = createRecorteExtracaoForm.fields.Filtros?.dataNascimento;

        if (dataNascimentoFromForm &&
            (dataNascimentoFromForm.min || dataNascimentoFromForm.max)) {
            setSelectedDataNascimento(dataNascimentoFromForm);

            if (dataNascimentoFromForm.min) minFieldTouched.current = true;
            if (dataNascimentoFromForm.max) maxFieldTouched.current = true;
        } else {
            setSelectedDataNascimento({});
            minFieldTouched.current = false;
            maxFieldTouched.current = false;
        }
    }, [createRecorteExtracaoForm.fields.Filtros?.dataNascimento]);

    // Função para verificar se a data é válida e completa
    const isValidCompleteDate = (date: Date | null | undefined): boolean => {
        if (!date) return false;

        if (isNaN(date.getTime())) return false;

        const year = date.getFullYear();
        return year >= 1000 && year <= 9999;
    }

    // Função para sincronizar com o estado global
    const syncWithGlobalState = useCallback((data: IDataNascimentoFilter) => {
        const hasValidMin = isValidCompleteDate(data.min);
        const hasValidMax = isValidCompleteDate(data.max);

        // Verifica se os campos foram tocados e se estão válidos
        const minReadyToSync = !minFieldTouched.current || hasValidMin;
        const maxReadyToSync = !maxFieldTouched.current || hasValidMax;

        // Se algum campo foi tocado mas não está completo, não sincroniza
        if (!minReadyToSync || !maxReadyToSync) {
            return;
        }

        // Se não há dados válidos, remove o filtro
        if (!hasValidMin && !hasValidMax) {
            removeFilterFromForm({
                filtro: 'dataNascimento',
                formType: 'create'
            });
            return;
        }

        // Valida as regras de negócio (neste ponto ambos os campos estão preenchidos)
        if (data.min! < minDate) {
            setAlert({
                message: 'A pessoa deve ter no máximo 120 anos',
                type: 'error'
            })
            return;
        }

        if (data.max! > maxDate) {
            setAlert({
                message: 'A pessoa deve ter no mínimo 18 anos',
                type: 'error'
            })
            return;
        }

        if (data.max! < data.min!) {
            setAlert({
                message: 'Data invalida',
                type: 'error'
            })
            return;
        }

        syncFilterWithForm({
            filtro: 'dataNascimento',
            value: data,
            formType: 'create'
        });
    }, [setAlert, syncFilterWithForm, removeFilterFromForm, minDate, maxDate])

    // Effect para validar quando os campos mudarem
    useEffect(() => {
        // Só tenta sincronizar se ambos os campos foram tocados
        if (!minFieldTouched.current || !maxFieldTouched.current) {
            return;
        }

        const timer = setTimeout(() => {
            syncWithGlobalState(selectedDataNascimento);
        }, 300);

        return () => clearTimeout(timer);
    }, [selectedDataNascimento, syncWithGlobalState])

    // Função para lidar com a mudança de data
    const handleDateChange = useCallback((field: 'min' | 'max') =>
        (newValue: Date | null, context: any) => {
            if (context.validationError === 'invalidDate') {
                return;
            }

            if (isValidCompleteDate(newValue)) {
                if (field === 'min') minFieldTouched.current = true
                if (field === 'max') maxFieldTouched.current = true
            }

            setSelectedDataNascimento(prev => ({
                ...prev,
                [field]: newValue || undefined
            }));
        }
        , [])

    return (
        <>
            <Grid item xs={5.5} my={2} px={2}>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ptBR}
                >
                    <DatePicker
                        sx={{ width: '100%' }}
                        label="Data início"
                        disableFuture
                        minDate={minDate}
                        maxDate={maxDate}
                        value={selectedDataNascimento.min ? new Date(selectedDataNascimento.min) : null}
                        onChange={handleDateChange('min')}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid item xs={5.5} my={2} px={2}>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ptBR}
                >
                    <DatePicker
                        sx={{ width: '100%' }}
                        label="Data fim"
                        disableFuture
                        minDate={minDate}
                        maxDate={maxDate}
                        value={selectedDataNascimento.max ? new Date(selectedDataNascimento.max) : null}
                        onChange={handleDateChange('max')}
                    />
                </LocalizationProvider>
            </Grid>
        </>
    )
}